import React, { useState, useEffect } from 'react';
import { sbAuth, SB_URL, SB_KEY } from './App';
import './Auth.css';

export default function Auth({ onLogin }) {
  const [isSignUp, setIsSignUp]         = useState(false);
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);

  // ── Handle Supabase OAuth callback (hash fragment) ───────────────────────
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params         = new URLSearchParams(hash.slice(1));
      const accessToken    = params.get('access_token');
      const refreshToken   = params.get('refresh_token');
      const providerToken  = params.get('provider_token');
      const providerRefresh= params.get('provider_refresh_token');

      if (accessToken) {
        if (providerToken)  localStorage.setItem('pm_google_token', providerToken);
        if (providerRefresh) localStorage.setItem('pm_google_refresh', providerRefresh);

        fetch(`${SB_URL}/auth/v1/user`, {
          headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${accessToken}` },
        })
          .then(r => r.json())
          .then(user => {
            window.history.replaceState({}, '', '/');
            onLogin(accessToken, refreshToken || '', user);
          })
          .catch(() => {
            setError('Google sign-in error. Please try again.');
            window.history.replaceState({}, '', '/');
          });
      }
    }

    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err) {
      setError(`Sign-in failed: ${err.replace(/_/g, ' ')}`);
      window.history.replaceState({}, '', '/');
    }
  }, [onLogin]);

  // ── Google Sign In via Supabase ───────────────────────────────────────────
  function handleGoogleSignIn() {
    const redirectTo = 'https://app.wavecanon.com';
    const scopes = [
      'https://www.googleapis.com/auth/drive.file',
      'https://www.googleapis.com/auth/spreadsheets',
      'https://www.googleapis.com/auth/calendar.events',
    ].join(' ');
    window.location.href = `${SB_URL}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(redirectTo)}&scopes=${encodeURIComponent(scopes)}&access_type=offline&prompt=consent`;
  }
  async function handleSubmit() {
    setError('');
    if (!email || !password) { setError('Email and password required.'); return; }
    if (isSignUp && !ageConfirmed) { setError('Please confirm your age and professional use before creating an account.'); return; }
    setLoading(true);
    let data;
    if (isSignUp) {
      data = await sbAuth('signup', { email, password, data: { age_confirmed: true, age_confirmed_at: new Date().toISOString() } });
    } else {
      data = await sbAuth('token?grant_type=password', { email, password });
    }
    setLoading(false);
    if (data.error || data.error_description || data.msg) {
      setError(data.error_description || data.error || data.msg);
      return;
    }
    onLogin(data.access_token, data.refresh_token, data.user);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSubmit();
  }

  return (
    <div className="auth-screen">
      <div className="auth-brand">
        <span className="auth-brand-mark" aria-hidden="true" />
        <span className="auth-brand-wordmark" role="img" aria-label="Canjur" />
      </div>

      {/* Google Sign In */}
      <button className="auth-google-btn" onClick={handleGoogleSignIn}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Sign in with Google
      </button>

      <div className="auth-divider">
        <span>or</span>
      </div>

      {/* Email/Password form */}
      <div className="auth-form">
        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="email"
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="current-password"
        />
        {isSignUp && (
          <label className="auth-age-label">
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={e => setAgeConfirmed(e.target.checked)}
            />
            <span>I confirm I am 18 years of age or older and am using this service in a professional capacity.</span>
          </label>
        )}
        {error && <div className="auth-error">{error}</div>}
        <button className="auth-btn" onClick={handleSubmit} disabled={loading || (isSignUp && !ageConfirmed)}>
          {loading ? '...' : isSignUp ? 'Create Account' : 'Sign In'}
        </button>
        <button className="auth-toggle" onClick={() => { setIsSignUp(!isSignUp); setError(''); }}>
          {isSignUp ? 'Have an account? ' : 'No account? '}
          <span>{isSignUp ? 'Sign in' : 'Create one'}</span>
        </button>
      </div>
    </div>
  );
}
